/**
 * FRONT-1550: shared "where did this conversation window get opened from"
 * helpers.
 *
 * The bug this fixes: opening a chat/coding/document window from a non-Home
 * page first hops to Home (the only mounted host for the cross-page open
 * request lives inside `home.tsx` / `chat-launcher.tsx`), then closing the
 * window left the user stranded on Home instead of back where they started.
 * The fix carries the origin path on the OPEN request itself, so its
 * lifetime is exactly the modal's — no standalone store, no stale-return
 * hazard — and the host that owns the modal navigates back to it on close.
 *
 * Moved here from `features/inspector/background-tasks.tsx` (its original
 * home, FRONT-634) so a lightweight, lazily-loaded surface (the Sketches
 * gallery, `features/sketches/sketches-gallery.tsx`) can reuse `isHomeRoute`
 * without dragging that module's own queries into its chunk (see the FRONT-1501 review note that used to justify a second,
 * local copy there — a dedicated pure module makes that copy unnecessary).
 */

/** `/` and `/chat` both render `home.tsx` (see routes.tsx) so either counts
 *  as "already home" — no navigation needed there. */
export function isHomeRoute(pathname: string): boolean {
  return pathname === '/' || pathname === '/chat';
}

/**
 * The path (+ search + hash) to return to when a conversation window opened
 * from `location` is later closed — or `null` when `location` is already
 * Home, meaning there is nothing to return to and close should behave
 * exactly as it always has.
 */
export function originToReturnTo(location: {
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  if (isHomeRoute(location.pathname)) return null;
  return `${location.pathname}${location.search}${location.hash}`;
}
